import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DeleteModal from 'Components/Common/DeleteModal';
// import { isEmpty } from 'lodash';

// Export Modal
// import ExportCSVModal from 'Components/Common/ExportCSVModal';

import { useDispatch } from 'react-redux';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { SelectOptions } from 'types/productTypes';
import {
  ActiveStatusOptions,
  PromotionActionButtons,
  PromotionButtonIconSize,
  PromoTypes,
} from 'pages/Promotions/PromoConstant';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { createSortableHeader } from 'Components/pos2u/CreateSortableHeader';
import {
  PromoFromAPI,
  PromotionSearchFilters,
} from 'types/promotion/promotionTypes';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  deletePromotionBatch,
  getPromotionsList,
} from 'services/promotionService';
import {
  resetPromotion,
  setCurrentPage,
  setEntriesPerPage,
  setInputPageValue,
  setPageFilters,
} from 'slices/promotions/reducer';
import { PROMOTIONS } from 'Routes/constants';
import MainAddButton from 'Components/pos2u/Buttons/MainAddButton';
import IconActionButton from 'Components/pos2u/Buttons/IconActionButton';
import { FaRegPenToSquare } from 'react-icons/fa6';
import { formatDaysText } from 'utils/common/Time';
import useWindowSize from 'Components/Hooks/WindowSizeHook';
import TableWrapper from 'Components/pos2u/TableWrapper';
import TextSelectedItems from 'Components/pos2u/TextSelectedItems';
import PromotionSearchForm from './PromotionSearchForm';
import MainDeleteButton from 'Components/pos2u/Buttons/MainDeleteButton';

const PromotionsPage = () => {
  const { t } = useTranslation();
  const history = useNavigate();
  const dispatch = useDispatch();

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';

  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded } = infoState;

  const PromotionState = useSelector((state: RootState) => state.Promotions);
  const { PageDetails, PageFilters } = PromotionState;

  const columnHelper = createColumnHelper<PromoFromAPI>();

  const [promoList, setPromoList] = useState<PromoFromAPI[]>([]);
  const [promoTotal, setPromoTotal] = useState<number>(0);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);

  const fetchAllPromotions = useCallback(async () => {
    if (!merchant?.id) return;
    setIsLoadSuccess(false);

    try {
      const rs = await getPromotionsList(
        // [merchant.id],
        [],
        PageFilters.promotionName,
        PageFilters.promoStatus === '' ? null : Number(PageFilters.promoStatus),
        PageFilters.promoType === '' ? null : Number(PageFilters.promoType),
        PageDetails.entriesPerPage,
        PageDetails.currentPage + 1
      );
      if (rs) {
        const { promotions, total } = rs;
        setPromoTotal(total);
        setPromoList(promotions);
        setIsLoadSuccess(true);
      }
    } catch (error) {
      console.error('Failed to fetch stores info:', error);
    }
  }, [
    PageDetails.currentPage,
    PageFilters,
    PageDetails.entriesPerPage,
    merchant,
  ]);

  useEffect(() => {
    if (isMerchantLoaded && merchant?.id) {
      fetchAllPromotions();
    }
  }, [merchant, isMerchantLoaded, fetchAllPromotions]);

  useEffect(() => {
    if (promoList) {
      setIsLoadSuccess(true);
    } else {
      setIsLoadSuccess(false);
    }
  }, [promoList]);

  const [deleteModalMulti, setDeleteModalMulti] = useState<boolean>(false);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState<
    number[]
  >([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] =
    useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);

  const deleteMultiple = async () => {
    const checkall = document.getElementById('checkBoxAll') as HTMLInputElement;
    if (selectedCheckBoxDelete && selectedCheckBoxDelete.length > 0) {
      const response: any = await deletePromotionBatch(selectedCheckBoxDelete);
      const { message, error_code, data } = response;
      if (message === 'OK' && error_code === 0) {
        toast.success(data.message, { autoClose: 3000 });
        fetchAllPromotions();
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    }
    setSelectedCheckBoxDelete([]);
    setIsCheckAll(false);
    setIsMultiDeleteButton(false);
    if (checkall) {
      checkall.checked = false;
    }
  };

  const checkedAllExcludeDisable = useCallback(() => {
    const updatedCheckAllState = !isCheckAll;
    setIsCheckAll(updatedCheckAllState);
    const checkboxes =
      document.querySelectorAll<HTMLInputElement>('.promoCheckBox');

    const tmp: number[] = [];

    checkboxes.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = updatedCheckAllState;
        if (updatedCheckAllState) {
          tmp.push(parseInt(checkbox.value, 10));
        }
      }
    });

    const checkedElements = document.querySelectorAll(
      '.promoCheckBox:checked:not(:disabled)'
    );
    setIsMultiDeleteButton(checkedElements.length > 0);
    setSelectedCheckBoxDelete(tmp);
  }, [isCheckAll]);

  const handleCheckboxChange = (id: number) => {
    const ele = document.querySelectorAll('.promoCheckBox:checked');
    if (ele.length > 0) {
      setIsMultiDeleteButton(true);
    } else {
      setIsMultiDeleteButton(false);
    }

    setSelectedCheckBoxDelete((prevSelected: number[]) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId: number) => selectedId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  // Column
  const PromoColumns: ColumnDef<PromoFromAPI, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: '#',
        header: () => (
          <input
            type="checkbox"
            id="checkBoxAll"
            data-testid="checkbox-all"
            className="form-check-input"
            onClick={() => checkedAllExcludeDisable()}
            checked={isCheckAll}
          />
        ),
        cell: (cell) => (
          <div id={`btn-del-${cell.row.original.id}`}>
            <input
              type="checkbox"
              className="promoCheckBox form-check-input"
              value={cell.row.original.id}
              data-testid={`checkbox-promo-${cell.row.original.id}`}
              // onChange={() => deleteCheckbox()}
              checked={selectedCheckBoxDelete.includes(cell.row.original.id)}
              disabled={cell.row.original.active === 1}
              onChange={() => handleCheckboxChange(cell.row.original.id)}
            />
            {cell.row.original.active === 1 && (
              <UncontrolledTooltip
                placement="top"
                target={`btn-del-${cell.row.original.id}`}
              >
                {t('The promotion is being used.')}
              </UncontrolledTooltip>
            )}
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('name', {
        header: ({ column }) => createSortableHeader(t('promoName'), column),
        cell: (cell) => cell.getValue(),
        enableColumnFilter: false,
      }),
      columnHelper.accessor('promotion_type', {
        header: ({ column }) => createSortableHeader(t('promoType'), column),
        cell: (cell) => {
          const typeVal = cell.getValue().toString();
          const getType = PromoTypes(t, true)
            .flatMap((group: SelectOptions) => group.options || [])
            .find((option) => option.value === typeVal);
          return getType?.label || '';
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'not_limit_date',
        header: t('Promo_Period'),
        cell: (cell) => {
          const rowData = cell.row.original;
          const { not_limit_date, start_date, end_date, time_start, time_end } =
            rowData;
          if (not_limit_date === 1) return 'ไม่กำหนดช่วงเวลา';
          return `${start_date} ${time_start} - ${end_date} ${time_end}`;

          // const formatDateWithDay = (dateStr: string, timeStr: string) => {
          //   const date = new Date(`${dateStr}T${timeStr}`);

          //   // Get weekday in English (always consistent key for t)
          //   const weekday = new Intl.DateTimeFormat('en-US', {
          //     weekday: 'long',
          //   }).format(date);

          //   // Format as DD/MM/YYYY HH:mm:ss
          //   const dd = String(date.getDate()).padStart(2, '0');
          //   const mm = String(date.getMonth() + 1).padStart(2, '0');
          //   const yyyy = date.getFullYear();
          //   const hh = String(date.getHours()).padStart(2, '0');
          //   const min = String(date.getMinutes()).padStart(2, '0');
          //   const ss = String(date.getSeconds()).padStart(2, '0');

          //   const formattedDate = `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;

          //   return `${t(weekday)}, ${formattedDate}`;
          // };

          // const startFormatted = formatDateWithDay(start_date, time_start);
          // const endFormatted = formatDateWithDay(end_date, time_end);

          // return <div>{`${startFormatted} - ${endFormatted}`}</div>;
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'days',
        header: t('Active Days'),
        cell: (cell) => {
          const rowData = cell.row.original;
          const str = formatDaysText(rowData.days, t);
          return rowData.not_limit_date === 1 ? '-' : str;
        },
        meta: {
          cellStyle: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            alignContent: 'flex-start',
            minWidth: isMobile ? '100px' : '160px',
            maxWidth: isMobile ? '120px' : '180px',
          },
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor('active', {
        header: ({ column }) => createSortableHeader(t('Status'), column),
        cell: (cell) => {
          const cellVal = cell.getValue().toString();
          const statStyle = cellVal === '1' ? 'success' : 'danger';
          const getStatus = ActiveStatusOptions(t).filter(
            (status) => status.value === cellVal
          );
          const statText = getStatus[0]?.label || '';
          return (
            <div className="mb-0 mt-0">
              <h5 className="mb-0 mt-0">
                <span
                  className={`badge bg-${statStyle}-subtle text-${statStyle}`}
                  style={{
                    width: '8rem',
                    objectFit: 'cover',
                    padding: '0.8rem 0.2rem',
                    borderRadius: '0.25rem',
                  }}
                >
                  {statText}
                </span>
              </h5>
            </div>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'action',
        header: 'Action',
        cell: (cell) => (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
              {/* <button
                type="button"
                className="btn btn-success btn-xl"
                id={`edit-btn-${cell.row.original.id}`}
                onClick={() => {
                  const promoData = cell.row.original;
                  history(`${PROMOTIONS.LIST}/${promoData.id}`);
                }}
              >
                {t('Edit')}
              </button> */}
              <IconActionButton
                id={`edit-btn-${cell.row.original.id}`}
                onClick={() => {
                  const promoData = cell.row.original;
                  history(`${PROMOTIONS.LIST}/${promoData.id}`);
                }}
                icon={
                  <FaRegPenToSquare fontSize={PromotionActionButtons.icon} />
                }
                title={t('Edit')}
                height={PromotionActionButtons.button.height}
                width={PromotionActionButtons.button.width}
              />
            </li>
          </ul>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [
      checkedAllExcludeDisable,
      isCheckAll,
      t,
      history,
      columnHelper,
      selectedCheckBoxDelete,
      isMobile,
    ]
  );

  // Export Modal
  // const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  document.title = t('Promotion') + ' | POS2U';

  const totalPages = Math.ceil(promoTotal / PageDetails.entriesPerPage);

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };

  const handlePromotionSearchSubmit = (searchFilters: {
    promotionName: string;
    promoType: string;
    promoStatus: string;
  }) => {
    setIsCheckAll(false);
    setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete([]);
    dispatch(
      setPageFilters((prev: PromotionSearchFilters) => ({
        ...prev,
        ...searchFilters,
      }))
    );
    dispatch(setCurrentPage(0));
    // setIsLoadSuccess(false);
  };

  return (
    <div className="page-content">
      {/* <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={orders}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        onCloseClick={() => setDeleteModal(false)}
      /> */}

      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
        title={t('Delete Promotion')}
        message={
          selectedCheckBoxDelete.length === 1
            ? t('SingleDeleteAlertMessage')
            : t('MultiDeleteAlertMessage', {
                items: selectedCheckBoxDelete.length,
              })
        }
        cancelBtnMsg={t('Cancel')}
        confirmBtnMsg={t('Delete')}
      />
      <Container fluid>
        <BreadCrumb title={t('PromotionList')} pageTitle={t('Promotion')} />
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">{t('PromotionList')}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {/* <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="add-promo-btn"
                        onClick={() => {
                          // setIsEdit(false);
                          // toggle();
                          dispatch(resetPromotion());
                          history(PROMOTIONS.ADD);
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {t('Add_Promotion')}
                      </button> */}
                      <MainAddButton
                        to=""
                        label={t('Add_Promotion')}
                        id="add-promo-btn"
                        onClick={() => {
                          dispatch(resetPromotion());
                          history(PROMOTIONS.ADD);
                        }}
                        soft={true}
                      />{' '}
                      {/* <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{' '}
                        Export
                      </button>{' '} */}
                      {isMultiDeleteButton && (
                        // <button
                        //   className="btn btn-soft-danger"
                        //   data-testid="multi-delete-button"
                        //   onClick={() => setDeleteModalMulti(true)}
                        // >
                        //   <RiDeleteBin2Line size={PromotionButtonIconSize} />
                        // </button>
                        <MainDeleteButton
                          onClick={() => setDeleteModalMulti(true)}
                          iconSize={PromotionButtonIconSize}
                        />
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="border border-dashed border-end-0 border-start-0 mb-3">
                <PromotionSearchForm
                  isDarkMode={isDarkMode}
                  onSubmit={handlePromotionSearchSubmit}
                />

                {selectedCheckBoxDelete && selectedCheckBoxDelete.length > 0 ? (
                  <TextSelectedItems
                    selectedItems={selectedCheckBoxDelete.length}
                  />
                ) : null}
              </CardBody>

              <CardBody className="pt-0">
                <div>
                  {/* {isLoadSuccess ? (
                    <TableContainerPOS2U
                      columns={PromoColumns}
                      data={promoList || []}
                      customPageSize={PageDetails.entriesPerPage}
                      divClass="table-responsive table-card mb-1 mt-0"
                      tableClass="align-middle table-nowrap"
                      theadClass={`table-${
                        isDarkMode ? 'dark' : 'light'
                      } text-muted text-uppercase`}
                      isPromoQuery={true}
                      SearchPlaceholder={t('SearchPromoName')}
                      selectedCheckBoxDelete={selectedCheckBoxDelete.length}
                      TotalData={promoTotal}
                      totalPages={totalPages}
                      setEntriesPerPage={setEntriesPerPage}
                      currentPage={PageDetails.currentPage}
                      handlePageChange={handlePageChange}
                      handleSearchFiltersSubmit={handlePromotionSearchSubmit}
                      inputPageValue={PageDetails.inputPageValue}
                      setInputPageValue={setInputPageValue}
                    />
                    ) : (
                      <Loader />
                      )} */}
                  <TableWrapper
                    columns={PromoColumns}
                    data={promoList || []}
                    customPageSize={PageDetails.entriesPerPage}
                    divClass="table-responsive table-card mb-1 mt-0"
                    tableClass="align-middle table-nowrap"
                    theadClass={`table-${
                      isDarkMode ? 'dark' : 'light'
                    } text-muted text-uppercase`}
                    TotalData={promoTotal}
                    totalPages={totalPages}
                    setEntriesPerPage={setEntriesPerPage}
                    currentPage={PageDetails.currentPage}
                    handlePageChange={handlePageChange}
                    inputPageValue={PageDetails.inputPageValue}
                    setInputPageValue={setInputPageValue}
                    isTableLoadSuccess={isLoadSuccess}
                  />
                </div>
                <ToastContainer closeButton={false} limit={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PromotionsPage;
